import type React from 'react';
import { useField } from 'formik';
import type { LucideIcon } from 'lucide-react';
import { getFormFieldClasses } from '@/utility/utility';
import { FieldWrapper, type FieldWrapperConfig } from './FieldWrapper';
import { MaskedInput } from '@/components/MaskedInput';

/**
 * Props for the FormField component
 */
interface MaskedFieldProps extends FieldWrapperConfig {
  IconSuffix?: LucideIcon | null;
  IconPrefix?: LucideIcon | null;
  className?: string;
  placeholder?: string;
  type?: string; // can be "text", "textarea", etc.
  onIconClick?: () => void;
  mask?: string;
}

/**
 * A reusable form field component that wraps an Input or Textarea component,
 * along with error handling and a label.
 */
export const MaskedField: React.FC<MaskedFieldProps> = ({
  name,
  placeholder,
  type = 'text',
  className = '',
  disabled = false,
  IconSuffix,
  IconPrefix,
  onIconClick,
  mask,
  ...wrapperProps
}) => {
  const [field, meta] = useField(name);
  className = getFormFieldClasses({ className, disabled, meta });

  const fieldWrapperProps = {
    name,
    disabled,
    errorMeta: meta,
    ...wrapperProps,
  };
  return (
    <FieldWrapper {...fieldWrapperProps}>
      <MaskedInput
        {...field}
        id={name}
        type={type}
        placeholder={placeholder}
        value={field.value ?? ''}
        className={className}
        disabled={disabled}
        IconPrefix={IconPrefix}
        IconSuffix={IconSuffix}
        onIconClick={onIconClick}
        mask={mask}
      />
    </FieldWrapper>
  );
};
